import { mapAuthError, waitOutRateLimit } from '@flashka/shared/auth';
import { useRateLimitWait } from '@flashka/shared/react/use-rate-limit-wait';

import { useState } from 'react';

type AuthActionError = { code?: string; status?: number } | null;

/**
 * Submit wrapper shared by the Better Auth forms (AuthCard, the account
 * dialog's profile/security tabs): busy flag + mapped error key. Each form
 * section calls it independently so a busy/failed action doesn't disable
 * unrelated controls.
 *
 * `run` must never leave `busy` stuck: `@better-fetch` REJECTS on
 * network-level failures (offline, DNS, CORS) instead of resolving
 * `{ error }`, so the rejection is folded into the same error surface the
 * server-side codes use.
 *
 * A 429 is not an error here until it has been waited out: `run` makes the
 * same call again while the worker's rate limiter refuses it (shared
 * `waitOutRateLimit` — the refusal ran nothing, so repeating it is safe), and
 * `wait.seconds` is the countdown a form shows meanwhile. Only a limit that
 * outlasts the budget reaches the user as `auth.errors.rateLimited`.
 *
 * `initialErrorKey` is a lazy initializer (useState semantics) — callers that
 * derive a starting error from the URL run that work exactly once.
 */
export function useAuthAction(initialErrorKey?: () => string | null) {
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(initialErrorKey ?? null);
  const wait = useRateLimitWait();

  async function run(
    action: () => Promise<{ error: AuthActionError }>,
  ): Promise<boolean> {
    setBusy(true);
    setErrorKey(null);
    try {
      const { error } = await waitOutRateLimit(action, wait.options);
      if (error) {
        setErrorKey(mapAuthError(error));
        return false;
      }
      return true;
    } catch (err) {
      // A rejection carries no Better Auth error shape, so there is no code
      // to map — log it for operators and show the generic copy.
      console.error('[auth] request failed:', err);
      setErrorKey('auth.errors.generic');
      return false;
    } finally {
      wait.clear();
      setBusy(false);
    }
  }

  return { busy, errorKey, run, setErrorKey, wait };
}
